
import React from "react";
import { Schemes } from "../../../../utils";
import { i18nManager } from "../../../../i18n";
import { Elements } from "../../../shared/Elements";
import MediaInput from "../../../shared/MediaInput";
import StatefulLink from "../../../shared/StatefulLink";
import { ConfirmDialog, Siren } from "../../../shared/ConfirmDialog";
import LoadingGif from "../../../../assets/images/loading_circle.gif";
import { Button, ButtonManageRef, Checkbox, Form } from "@rivtn/noseur";
import { BaseService, ConsusService, IMember, IProfileWebsite, IUploadedFile, MemberService, SanitizedResponse } from "../../../../services";

export interface HomeProps {
    profile: IMember;
}

function Home(props: HomeProps) {

    const consusService = ConsusService.getInstance();
    const memberService = MemberService.getInstance();
    const [profile, _] = React.useState<IMember>(props.profile);
    const labels = i18nManager.Labels.account.profile_website.home;
    const [profileWebsite, setProfileWebsite] = React.useState<IProfileWebsite>();
    const maxSponsors = 5; // TODO FETCH FROM PLAN

    React.useEffect(() => {
        memberService.getProfileWebsite().then(({ sanitized }: SanitizedResponse<IProfileWebsite>) => {
            setProfileWebsite(sanitized);
        }).catch(BaseService.reportError);
    }, []);

    return (<div className="account-setting">
        <div className="header">
            <span className="title">{i18nManager.Labels.account.profile_website.title} - {labels.title}</span>
        </div>
        {!profileWebsite
            ? (<div style={{ margin: "90px 110px" }}>
                <img alt={"loading"} src={LoadingGif} style={{ width: 80, margin: "auto" }} />
            </div>)
            : (<Form scheme={Schemes.OMNEHOST} className="profile-settings">
                <Checkbox label={labels.enable_ad} defaultChecked={profileWebsite?.home_settings?.enable_rivtn_ad} onChange={(e: any) => {
                    setProfileWebsite({ ...profileWebsite, home_settings: { ...profileWebsite.home_settings, enable_rivtn_ad: e.checked } })
                }} />
                <hr />
                <Checkbox label={labels.show_twitter_feed} defaultChecked={profileWebsite?.home_settings?.show_twitter_feeds} onChange={(e: any) => {
                    setProfileWebsite({ ...profileWebsite, home_settings: { ...profileWebsite.home_settings, show_twitter_feeds: e.checked } })
                }} />
                <Checkbox label={labels.show_twitch_stream} defaultChecked={profileWebsite?.home_settings?.show_twitch_streams} onChange={(e: any) => {
                    setProfileWebsite({ ...profileWebsite, home_settings: { ...profileWebsite.home_settings, show_twitch_streams: e.checked } })
                }} />
                <Checkbox label={labels.show_instagram_feed} defaultChecked={profileWebsite?.home_settings?.show_instagram_feeds} onChange={(e: any) => {
                    setProfileWebsite({ ...profileWebsite, home_settings: { ...profileWebsite.home_settings, show_instagram_feeds: e.checked } })
                }} />
                <Checkbox label={labels.show_top_activities} defaultChecked={profileWebsite?.home_settings?.show_top_activities} onChange={(e: any) => {
                    setProfileWebsite({ ...profileWebsite, home_settings: { ...profileWebsite.home_settings, show_top_activities: e.checked } })
                }} />
                <Checkbox label={labels.show_schedules} defaultChecked={profileWebsite?.home_settings?.show_schedules} onChange={(e: any) => {
                    setProfileWebsite({ ...profileWebsite, home_settings: { ...profileWebsite.home_settings, show_schedules: e.checked } })
                }} />
                <Checkbox label={labels.show_top_gallery} defaultChecked={profileWebsite?.home_settings?.show_top_gallery} onChange={(e: any) => {
                    setProfileWebsite({ ...profileWebsite, home_settings: { ...profileWebsite.home_settings, show_top_gallery: e.checked } })
                }} />
                <Checkbox label={labels.show_sponsor} defaultChecked={profileWebsite?.home_settings?.show_sponsors} onChange={(e: any) => {
                    setProfileWebsite({ ...profileWebsite, home_settings: { ...profileWebsite.home_settings, show_sponsors: e.checked } })
                }} />
                <hr />
                <div className="sponsors-logos">
                    <span>{labels.upload_sponsor_logos}</span>
                    <div className="logos">
                        {profileWebsite.sponsors?.length < maxSponsors && (<i className="fa fa-plus" onClick={uploadSponsorLogo} />)}
                        {profileWebsite.sponsors?.map((sponsor) => (<div key={sponsor}>
                            <img src={sponsor} alt="sponsor-logo" />
                            <StatefulLink style={{ marginTop: 20 }} label={<i className="fa fa-times" />} onClick={(done) => {
                                deleteSponsorLogo(sponsor, done);
                            }} />
                        </div>))}
                    </div>
                </div>
                <hr style={{ marginTop: 0 }} />
                <Button scheme={Schemes.OMNEHOST} text={i18nManager.Labels.common.save_changes} loadingProps={Elements.BUTTON_LOADING_PROPS}
                    style={{ marginTop: 20 }} onClick={(e) => {
                        e.manageRef?.setLoadingState(true);
                        updateProfileWebsite(() => e.manageRef?.setLoadingState(false));
                    }} />
            </Form>)}
    </div>);

    function updateProfileWebsite(onDone?: () => void) {
        memberService.updateProfileWebsite(profileWebsite!).then(({ sanitized }: SanitizedResponse<IProfileWebsite>) => {
            setProfileWebsite(sanitized);
            Siren.alert(i18nManager.Labels.account.profile_website.update_successful);
        }).catch(BaseService.reportError).finally(onDone);
    }

    function uploadSponsorLogo() {
        if (!profileWebsite) return;
        const dialogErrorRef = { current: null } as any;
        const mediaInputManageRef = { current: undefined as any };

        const dialog = ConfirmDialog({
            closable: true,
            title: labels.dialog.upload_home_sponsor_logo,
            style: { minWidth: 400, maxWidth: "80%" },
            confirmLabel: i18nManager.Labels.common.upload,
            content: (<div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "30px 50px" }}>
                <MediaInput loading noControl manageRef={mediaInputManageRef} width={"100%"} height={"100%"} maxFileSize={20000000}
                    style={{ alignSelf: "center", borderWidth: 0, boxShadow: "none", marginTop: 10 }}
                    itemTemplate={(options) => {
                        return (<div className="sponsor-logo-preview" key={options.index}>
                            <div>
                                <span>{labels.dialog.small_preview}</span>
                                {options.index === -1 && (<div className="sp">{(options.file as any)}</div>)}
                                {options.index > -1 && (<img className="sp" alt="small preview" src={options.url} />)}
                            </div>
                            <div>
                                <span>{labels.dialog.medium_preview}</span>
                                {options.index === -1 && (<div className="mp">{(options.file as any)}</div>)}
                                {options.index > -1 && (<img className="mp" alt="small preview" src={options.url} />)}
                            </div>
                            <div>
                                <span>{labels.dialog.large_preview}</span>
                                {options.index === -1 && (<div className="lp">{(options.file as any)}</div>)}
                                {options.index > -1 && (<img className="lp" alt="small preview" src={options.url} />)}
                            </div>
                        </div>);
                    }}
                    onValidationFail={(_: File, error: string) => {
                        dialogErrorRef.current!.innerText = error;
                        return true;
                    }}
                    onGenerateWithAi={async () => {
                        return new File(["We are here"], "we.txt", { type: 'text/plain' });
                    }} />
                <StatefulLink style={{ marginTop: 20 }} label={labels.dialog.select_another} onClick={(done) => {
                    dialogErrorRef.current!.innerText = " ";
                    mediaInputManageRef.current?.select();
                    done();
                }} />
                <div ref={dialogErrorRef} className="error" style={{ alignSelf: "flex-start" }}> </div>
            </div>),
            onConfirm: (button?: ButtonManageRef) => {
                dialogErrorRef.current!.innerText = " ";
                const files = mediaInputManageRef.current?.files() as File[];
                if (!files.length) {
                    dialogErrorRef.current!.innerText = labels.dialog.no_media_selected;
                    return false;
                }
                const file = files[0];
                button?.setLoadingState(true);
                consusService.uploadFile(`/omnehost/member/sponsor_logo/`, `${profile.janus_user_external_id}_${file.name.substring(file.name.length - 10, file.name.length)}`, file).then(({ sanitized }: SanitizedResponse<IUploadedFile>) => {
                    if (!profileWebsite?.sponsors) {
                        profileWebsite.sponsors = [];
                    }
                    profileWebsite?.sponsors.push(sanitized.consus_download_url);
                    dialog.hide();
                    setProfileWebsite({ ...profileWebsite, sponsors: profileWebsite?.sponsors! });
                    updateProfileWebsite();
                }).catch(BaseService.reportError).finally(() => {
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show(() => mediaInputManageRef.current?.select());
    }

    function deleteSponsorLogo(url: string, done: () => void) {
        if (!profileWebsite) return;

        const removeMediaLink = () => {
            const sponsors = profileWebsite?.sponsors;
            sponsors?.splice(sponsors.indexOf(url), 1);
            setProfileWebsite({ ...profileWebsite, sponsors });
            updateProfileWebsite();
        };
        consusService.deleteFile([url]).then(removeMediaLink).catch((error: any) => {
            if (error.response.status === 404) {
                removeMediaLink();
                return;
            }
            BaseService.reportError(error);
        }).finally(done);
    }

}

export default Home;
